/**
 * Which season a date belongs to, and how that season is named.
 *
 * Both sports need this and the two disagree. A football season is named for
 * the year it starts in, so the February 2025 Super Bowl belongs to the 2024
 * season. A basketball season spans the new year and ESPN names it for the
 * year it *ends* -- the 2026-27 season is `year: 2027` in every payload.
 * Getting this wrong files half a basketball season under the wrong label and,
 * worse, asks ESPN for a season that does not exist.
 */

import type { Sport } from './sports';

/** First month (1-based) of a new season, shared by both sports. */
const SEASON_START_MONTH = 8;

/**
 * The season year for an instant, in the sport's own numbering.
 *
 * Uses UTC, matching the ISO kickoff strings ESPN returns; a late-evening tip
 * can land on the next UTC day but never on the far side of the August
 * boundary, so the answer is unaffected.
 */
export function seasonYearOf(date: Date | string | number, sport: Sport = 'football'): number {
  const d = date instanceof Date ? date : new Date(date);
  if (Number.isNaN(d.getTime())) return NaN;
  const year = d.getUTCFullYear();
  const afterRollover = d.getUTCMonth() + 1 >= SEASON_START_MONTH;
  if (sport === 'basketball') {
    // Named for the year it ends: an October 2026 tip is season 2027, and so
    // is the following April.
    return afterRollover ? year + 1 : year;
  }
  return afterRollover ? year : year - 1;
}

/** The season in progress, or the one most recently finished. */
export function currentSeasonYear(sport: Sport = 'football', now: Date = new Date()): number {
  return seasonYearOf(now, sport);
}

/** "2026 season", or "2026-27 season". */
export function seasonLabel(year: number, sport: Sport = 'football'): string {
  if (sport === 'basketball') {
    return `${year - 1}-${String(year).slice(2)} season`;
  }
  return `${year} season`;
}

/**
 * Whether a schedule we hold is for the season currently in progress.
 *
 * When it is not, a team has no more games because the year has turned over
 * and the next schedule is not published yet -- a different situation, and a
 * different message, from a team whose postseason has simply not been drawn.
 */
export function isCurrentSeason(
  season: number | null,
  sport: Sport = 'football',
  now: Date = new Date(),
): boolean {
  return season !== null && season === currentSeasonYear(sport, now);
}

/** Heading for games with no fixture, and therefore no season. */
export const UNDATED_LABEL = 'No date set';

export interface SeasonGroup<T> {
  label: string;
  items: T[];
}

/**
 * Buckets items by season, most recent first, undated last.
 *
 * Grouped by the season's *label* rather than its number, because the two
 * sports number seasons differently and a raw year would collide: football's
 * 2026 and basketball's 2026 are a year apart in real time. Groups are ordered
 * by their most recent item, which keeps a mixed football-and-basketball list
 * in the order somebody actually lived through it.
 */
export function groupBySeason<T>(
  items: T[],
  seasonOf: (item: T) => { year: number | null; sport: Sport },
  recencyOf: (item: T) => number,
): SeasonGroup<T>[] {
  const buckets = new Map<string, { items: T[]; rank: number }>();

  for (const item of items) {
    const { year, sport } = seasonOf(item);
    const label = year === null ? UNDATED_LABEL : seasonLabel(year, sport);
    // Undated games sort last however recent they are: they have no place on a
    // timeline, and floating them to the top would bury the current season.
    const rank = year === null ? -Infinity : recencyOf(item);
    const bucket = buckets.get(label);
    if (bucket) {
      bucket.items.push(item);
      bucket.rank = Math.max(bucket.rank, rank);
    } else {
      buckets.set(label, { items: [item], rank });
    }
  }

  return [...buckets.entries()]
    .sort((a, b) => b[1].rank - a[1].rank)
    .map(([label, { items: grouped }]) => ({ label, items: grouped }));
}
